import { PDSRecord, Gender, PDSName, PDSAddress, PDSTelecom, PDSMeta } from '../../types';
import {
    FormattedPDSRecord,
    FormattedPDSName,
    FormattedPDSAddress,
    FormattedPDSContact,
} from '../Patient';
import { formatPhoneNumber, sortByPeriod, isAfter, isBefore } from '../../utils';

const genderMappings: { [key: string]: Gender } = {
    male: Gender.MALE,
    female: Gender.FEMALE,
    other: Gender.OTHER,
    unknown: Gender.NOTPROVIDED,
};

const sortNames = (names: PDSName[] = [], filterOldData?: boolean): FormattedPDSName[] => {
    const nameByUse: Record<string, FormattedPDSName[]> = {};

    names.forEach(({ given, family, use, period }) => {
        if (!filterOldData || !period?.end || isAfter(period.end, new Date())) {
            const [firstName, middleName] = given || '';
            const formattedRecord: FormattedPDSName = {
                firstName,
                middleName,
                lastName: family,
                use,
                period,
            };

            if (nameByUse[use]) {
                nameByUse[use].push(formattedRecord);
            } else {
                nameByUse[use] = [formattedRecord];
            }
        }
    });

    let orderedNames: FormattedPDSName[] = [];

    if (nameByUse.usual) {
        nameByUse.usual.sort(sortByPeriod);
        orderedNames = orderedNames.concat(nameByUse.usual);
    }

    if (nameByUse.nickname) {
        nameByUse.nickname.sort(sortByPeriod);
        orderedNames = orderedNames.concat(nameByUse.nickname);
    }

    if (nameByUse.temp) {
        nameByUse.temp.sort(sortByPeriod);
        orderedNames = orderedNames.concat(nameByUse.temp);
    }

    if (nameByUse.old) {
        nameByUse.old.sort(sortByPeriod);
        orderedNames = orderedNames.concat(nameByUse.old);
    }

    if (nameByUse.maiden) {
        nameByUse.maiden.sort(sortByPeriod);
        orderedNames = orderedNames.concat(nameByUse.maiden);
    }

    return orderedNames;
};

const sortAddresses = (
    addresses: PDSAddress[] = [],
    filterOldData?: boolean,
): FormattedPDSAddress[] => {
    const addressByUse: Record<string, FormattedPDSAddress[]> = {};

    addresses.forEach(({ line, postalCode, use, period }) => {
        if (!filterOldData || !period?.end || isAfter(period.end, new Date())) {
            const [addressLine1, addressLine2, addressLine3, town, county] = line;
            const swapLine3 = !!addressLine3 && !town;
            const townCounty = county ? `${town}, ${county}` : town;

            const formattedRecord: FormattedPDSAddress = {
                addressLine1,
                addressLine2,
                ...(!swapLine3 && { addressLine3 }),
                postCode: postalCode,
                use,
                period,
                // To match the PDS data up to the Doc Abode format, if there is a county, we just
                // append it to the town. This is because Doc Abode scheduling doesn't currently use a
                // `county` field.
                town: swapLine3 ? addressLine3 : townCounty,
            };

            if (addressByUse[use]) {
                addressByUse[use].push(formattedRecord);
            } else {
                addressByUse[use] = [formattedRecord];
            }
        }
    });

    let orderedAddresses: FormattedPDSAddress[] = [];

    if (addressByUse.billing) {
        addressByUse.billing.sort(sortByPeriod);
        orderedAddresses = orderedAddresses.concat(addressByUse.billing);
    }

    if (addressByUse.temp) {
        addressByUse.temp.sort(sortByPeriod);
        orderedAddresses = orderedAddresses.concat(addressByUse.temp);
    }

    if (addressByUse.home) {
        addressByUse.home.sort(sortByPeriod);
        orderedAddresses = orderedAddresses.concat(addressByUse.home);
    }

    if (addressByUse.work) {
        addressByUse.work.sort(sortByPeriod);
        orderedAddresses = orderedAddresses.concat(addressByUse.work);
    }

    return orderedAddresses;
};

const sortContactNumbers = (contactNumbers: PDSTelecom[] = [], filterOldData?: boolean) => {
    const contactByUse: Record<string, FormattedPDSContact[]> = {};
    let singleContactNumberSet = true;
    let numContactNumbers = 0;

    contactNumbers
        .filter((item) => item.system === 'phone' && item.value)
        .forEach(({ value, use, period }) => {
            if (!filterOldData || !period?.end || isAfter(period.end, new Date())) {
                const formattedRecord = {
                    value: value ? formatPhoneNumber(value) : '',
                    use: use || 'mobile',
                    period,
                };

                // create array if use case is new
                if (use && !Object.keys(contactByUse).includes(use)) {
                    contactByUse[use] = [];
                }

                if (!use && !Object.keys(contactByUse).includes('mobile')) {
                    contactByUse.mobile = [];
                }

                // push record to array
                if (use) {
                    contactByUse[use].push(formattedRecord);
                } else {
                    contactByUse.mobile.push(formattedRecord);
                }

                numContactNumbers += 1;
            }
        });

    if (numContactNumbers > 1) {
        singleContactNumberSet = false;
    }

    let orderedContacts: FormattedPDSContact[] = [];

    if (contactByUse.temp) {
        contactByUse.temp.sort(sortByPeriod);
        orderedContacts = orderedContacts.concat(contactByUse.temp);
    }

    if (contactByUse.mobile) {
        contactByUse.mobile.sort(sortByPeriod);
        orderedContacts = orderedContacts.concat(contactByUse.mobile);
    }

    if (contactByUse.home) {
        contactByUse.home.sort(sortByPeriod);
        orderedContacts = orderedContacts.concat(contactByUse.home);
    }

    if (contactByUse.work) {
        contactByUse.work.sort(sortByPeriod);
        orderedContacts = orderedContacts.concat(contactByUse.work);
    }

    return {
        singleContactNumberSet,
        contactNumber: orderedContacts,
    };
};

export const sanitiseSecurity = (meta: PDSMeta | undefined): { code: string; display: string } => {
    const defaultReturn = { code: 'U', display: 'unrestricted' };

    if (!meta) {
        return defaultReturn;
    }

    const codeSupplied = meta.security?.[0].code;
    if (!codeSupplied) {
        return defaultReturn;
    }

    // Does the code conform to the mapping
    const code = codeSupplied.toUpperCase();
    const codeMapping: { [key: string]: string } = {
        U: 'unrestricted',
        R: 'restricted',
        V: 'very restricted',
        REDACTED: 'redacted',
    };

    if (!Object.keys(codeMapping).includes(code)) {
        return defaultReturn;
    }

    // code conforms return the correct security
    return { code, display: codeMapping[code] };
};

export function formatPdsRecord(record: PDSRecord, filterOldData?: boolean): FormattedPDSRecord {
    let dateOfBirth;

    if (record.birthDate) {
        const [year, month = '01', day = '01'] = record.birthDate.split('-');
        dateOfBirth = `${year}-${month}-${day}T00:00:00Z`;
    }

    const gender = record.gender ? genderMappings[record.gender] : Gender.NOTPROVIDED;
    const name = sortNames(record.name, filterOldData);
    const address = sortAddresses(record.address, filterOldData);

    const { singleContactNumberSet, contactNumber } = sortContactNumbers(
        record.telecom,
        filterOldData,
    );

    const isDeceased = record.deceasedDateTime && isBefore(record.deceasedDateTime, new Date());

    const security = sanitiseSecurity(record.meta);

    return {
        versionId: record.meta?.versionId || '-1',
        nhsNumber: record.id,
        gender,
        dateOfBirth,
        name,
        address,
        contactNumber,
        ...(isDeceased && { deceasedDateTime: record.deceasedDateTime }),
        singleDataSet: {
            name: name.length === 1,
            address: address.length === 1,
            contactNumber: singleContactNumberSet,
        },
        security,
    };
}
